import { kml } from '@tmcw/togeojson';
import type { Feature, Geometry, Position } from 'geojson';

export type LatLng = google.maps.LatLngLiteral;

export type PolygonRings = LatLng[][];

export interface ParsedKml {
  polygons: PolygonRings[];
  points: LatLng[];
}

const toLatLng = ([lng, lat]: Position): LatLng => ({ lat, lng });

const isValidCoord = ([lng, lat]: Position): boolean =>
  Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);

const isValidRing = (ring?: Position[]): ring is Position[] =>
  Boolean(ring?.length);

const isClosedRing = (line: Position[]): boolean => {
  if (line.length < 4) return false;
  const [firstLng, firstLat] = line[0];
  const [lastLng, lastLat] = line[line.length - 1];
  return firstLng === lastLng && firstLat === lastLat;
};

const polygonsFromGeometry = (geometry: Geometry | null): Position[][][] => {
  if (!geometry) return [];

  switch (geometry.type) {
    case 'Polygon':
      return [geometry.coordinates.filter(isValidRing)];
    case 'MultiPolygon':
      return geometry.coordinates.map((polygon) => polygon.filter(isValidRing));
    case 'GeometryCollection':
      return geometry.geometries.flatMap(polygonsFromGeometry);
    case 'LineString':
      return isClosedRing(geometry.coordinates) ? [[geometry.coordinates]] : [];
    default:
      return [];
  }
};

export const parseKmlString = (kmlString?: string | null): ParsedKml => {
  const empty: ParsedKml = { polygons: [], points: [] };

  if (!kmlString || kmlString === '-') return empty;

  try {
    const dom = new DOMParser().parseFromString(kmlString, 'application/xml');

    if (dom.querySelector('parsererror')) return empty;

    const geojson = kml(dom);

    const polygons = geojson.features
      .flatMap((feature: Feature<Geometry | null>) =>
        polygonsFromGeometry(feature.geometry),
      )
      .map((rings) =>
        rings
          .map((ring) => ring.filter(isValidCoord).map(toLatLng))
          .filter((ring) => ring.length > 0),
      )
      .filter((rings) => rings.length > 0);

    return {
      polygons,
      points: polygons.flat(2),
    };
  } catch (error) {
    console.warn('Falha ao interpretar o KML da gleba.', error);
    return empty;
  }
};
